// ============================================================
// FeatureVideo.jsx — a short silent feature video on the marketing site.
//
// First impressions decide how this behaves:
//   - The page renders the POSTER first (a still taken from the video's
//     first frame, the same size as the video), so nothing moves when the
//     video starts and the page is as quick to paint as before.
//   - No video bytes are requested until the frame is near the screen.
//     The <source> elements are only added then, which also means a
//     crawler that reads the HTML never fetches a video.
//   - Once at least half of it is on screen it plays, muted and looping,
//     and it pauses again when scrolled away.
//   - A visible pause/play control (WCAG 2.2.2: moving content longer
//     than five seconds must be stoppable).
//   - It never autoplays for someone who asked for reduced motion, and if
//     the browser refuses to autoplay (iPhone Low Power Mode) it simply
//     shows the play control. Either way the still is there.
//   - One H.264 MP4, which every current browser plays (iPhone included).
//   - A full-screen control. Desktop and Android use the Fullscreen API on
//     the video itself and show the browser's own controls while there;
//     iPhone has no Fullscreen API for page elements, so it hands the video
//     to the native player (webkitEnterFullscreen). The control is only
//     shown where one of the two exists.
//   - Options for the homepage: className (the page's own frame),
//     deferUntilLoad (the hero waits for the page's load event before any
//     video bytes, so the still stays the page's first paint), and
//     showFullscreen={false} for a frame too small for two controls. Once
//     it first plays the wrapper carries data-started, which lets a page
//     fade the video in over its still.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import './FeatureVideo.css';

export const FEATURE_VIDEO_BASE = (process.env.REACT_APP_FEATURE_VIDEO_BASE || '').replace(/\/$/, '') + '/feature-videos';

// True while scripts/prerender-marketing.js is rendering the page to HTML:
// the pages then keep their stills, so the prerendered HTML carries no
// <video> (and no poster request before the page's own JavaScript runs).
export function isPrerender() {
  return typeof window !== 'undefined' && window.__CAREHUB_PRERENDER__ === true;
}

function fullscreenElement() {
  if (typeof document === 'undefined') return null;
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function prefersReducedMotion() {
  try {
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export default function FeatureVideo({
  id, width, height, alt, base = FEATURE_VIDEO_BASE, className = '', deferUntilLoad = false, showFullscreen = true,
}) {
  const ref = useRef(null);
  const [near, setNear] = useState(false);       // sources attached
  const [playing, setPlaying] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const reduced = useRef(prefersReducedMotion());
  const userPausedRef = useRef(false);
  const [canFullscreen, setCanFullscreen] = useState(false);
  const [started, setStarted] = useState(false);

  const tryPlay = useCallback(() => {
    const v = ref.current;
    if (!v || reduced.current || userPausedRef.current) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => setPlaying(false));
  }, []);

  // Attach the sources when the frame comes near, then play/pause on view.
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    let cancelled = false;
    const attach = () => { if (!cancelled) setNear(true); };
    const nearObs = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      nearObs.disconnect();
      if (deferUntilLoad && document.readyState !== 'complete') window.addEventListener('load', attach, { once: true });
      else attach();
    }, { rootMargin: '300px 0px' });
    nearObs.observe(v);
    const viewObs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (fullscreenElement() === v) continue; // full screen is not "scrolled away"
        if (e.intersectionRatio >= 0.5) tryPlay();
        else if (!v.paused) v.pause();
      }
    }, { threshold: [0, 0.5] });
    viewObs.observe(v);
    return () => { cancelled = true; window.removeEventListener('load', attach); nearObs.disconnect(); viewObs.disconnect(); };
  }, [tryPlay, deferUntilLoad]);

  // A <source> added after mount needs load() before it is used.
  useEffect(() => {
    const v = ref.current;
    if (!near || !v) return;
    v.load();
    const r = v.getBoundingClientRect();
    const onScreen = r.top < window.innerHeight && r.bottom > 0 && r.height > 0
      && (Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0)) / r.height >= 0.5;
    if (onScreen) tryPlay();
  }, [near, tryPlay]);

  // Decide after mount whether this browser can show the video full screen.
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    setCanFullscreen(showFullscreen && (typeof v.requestFullscreen === 'function'
      || typeof v.webkitRequestFullscreen === 'function'
      || typeof v.webkitEnterFullscreen === 'function'));
  }, [showFullscreen]);

  // While the video is full screen it gets the browser's own controls (so it
  // can be paused and scrubbed there); they go again when it returns.
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof document === 'undefined') return undefined;
    const onChange = () => { v.controls = fullscreenElement() === v; };
    const onIosEnd = () => { if (!userPausedRef.current) tryPlay(); };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    v.addEventListener('webkitendfullscreen', onIosEnd);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
      v.removeEventListener('webkitendfullscreen', onIosEnd);
    };
  }, [tryPlay]);

  const enlarge = () => {
    const v = ref.current;
    if (!v) return;
    if (!near) setNear(true);
    const request = v.requestFullscreen || v.webkitRequestFullscreen;
    if (typeof request === 'function') {
      const p = request.call(v);
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else if (typeof v.webkitEnterFullscreen === 'function') {
      try { v.webkitEnterFullscreen(); } catch { /* not loaded yet: the play control still works */ }
    }
    if (v.paused && !userPausedRef.current) {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  };

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      userPausedRef.current = false; setUserPaused(false);
      if (!near) setNear(true);
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      userPausedRef.current = true; setUserPaused(true);
      v.pause();
    }
  };

  return (
    <div className={`ftp__video${className ? ` ${className}` : ''}`} data-started={started ? 'true' : undefined}>
      <video
        ref={ref}
        className="ftp__video-el"
        muted
        loop
        playsInline
        preload="none"
        poster={`${base}/${id}.webp`}
        width={width}
        height={height}
        aria-label={alt}
        data-feature-video={id}
        onPlay={() => setPlaying(true)}
        onPlaying={() => setStarted(true)}
        onPause={() => setPlaying(false)}
      >
        {near && <source src={`${base}/${id}.mp4`} type="video/mp4" />}
      </video>
      <button
        type="button"
        className={`ftp__video-toggle ${playing ? 'is-playing' : ''}`}
        onClick={toggle}
        aria-label={playing ? 'Pause video' : 'Play video'}
        aria-pressed={!playing && userPaused ? true : undefined}
      >
        <span aria-hidden="true">{playing ? '❚❚' : '▶'}</span>
      </button>
      {canFullscreen && (
        <button
          type="button"
          className="ftp__video-toggle ftp__video-full"
          onClick={enlarge}
          aria-label="Show video full screen"
        >
          <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
          </svg>
        </button>
      )}
    </div>
  );
}
